import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import './Legal.css';

const PrivacyPolicy = () => {
  return (
    <>
      <SEO
        title="Privacy Policy"
        description="Privacy Policy for SilverFox Media. Learn how we collect, use, and protect your personal information."
        url="/privacy-policy"
      />

      {/* Hero Section */}
      <section className="legal-hero">
        <div className="legal-hero__bg">
          <div className="legal-hero__gradient-orb legal-hero__gradient-orb--1" />
          <div className="legal-hero__gradient-orb legal-hero__gradient-orb--2" />
        </div>
        <div className="container">
          <motion.div
            className="legal-hero__content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>Privacy Policy</h1>
            <p>Last updated: January 15, 2026</p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="legal-content section">
        <div className="container">
          <motion.div
            className="legal-content__wrapper"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="legal-section">
              <h2>1. Introduction</h2>
              <p>
                SilverFox Media ("we," "our," or "us") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website silverfoxmedia.co, purchase our templates, or use our services.
              </p>
              <p>
                By using our website or services, you consent to the data practices described in this policy.
              </p>
            </div>

            <div className="legal-section">
              <h2>2. Information We Collect</h2>

              <h3>Information You Provide</h3>
              <p>We collect information you voluntarily provide when you:</p>
              <ul>
                <li>Fill out our contact form (name, email, phone, company, message)</li>
                <li>Purchase a template (name, email, payment information via Stripe)</li>
                <li>Sign up for our services (business information, project details)</li>
                <li>Communicate with us via email or phone</li>
              </ul>

              <h3>Information Collected Automatically</h3>
              <p>When you visit our website, we automatically collect:</p>
              <ul>
                <li><strong>Device Information:</strong> Browser type, operating system, device type</li>
                <li><strong>Usage Data:</strong> Pages visited, time spent on pages, click patterns</li>
                <li><strong>Location Data:</strong> General geographic location based on IP address</li>
                <li><strong>Referral Data:</strong> How you found our website</li>
              </ul>

              <h3>Cookies and Tracking Technologies</h3>
              <p>We use cookies and similar technologies to:</p>
              <ul>
                <li>Analyze website traffic and usage patterns (Google Analytics)</li>
                <li>Remember your preferences</li>
                <li>Improve website performance</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>3. How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              <ul>
                <li>Respond to your inquiries and contact form submissions</li>
                <li>Process template purchases and deliver files</li>
                <li>Provide and improve our services</li>
                <li>Send service-related communications (order confirmations, project updates)</li>
                <li>Analyze website usage to improve user experience</li>
                <li>Comply with legal obligations</li>
                <li>Protect against fraudulent or unauthorized transactions</li>
              </ul>
              <p>
                <strong>We do not sell your personal information to third parties.</strong>
              </p>
            </div>

            <div className="legal-section">
              <h2>4. Third-Party Services</h2>
              <p>We use the following third-party services that may collect or process your data:</p>

              <h3>Stripe</h3>
              <p>
                We use Stripe to process payments. When you make a purchase, your payment information is collected and processed directly by Stripe. We do not store your full credit card information on our servers. Please review <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Stripe's Privacy Policy</a>.
              </p>

              <h3>SendGrid</h3>
              <p>
                We use SendGrid to send transactional emails (contact form notifications, order confirmations). Your email address and name may be processed by SendGrid. Please review <a href="https://sendgrid.com/policies/privacy/" target="_blank" rel="noopener noreferrer">SendGrid's Privacy Policy</a>.
              </p>

              <h3>Google Analytics</h3>
              <p>
                We use Google Analytics to analyze website traffic. This service collects anonymous usage data. You can opt out by installing the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Browser Add-on</a>.
              </p>

              <h3>Amazon Web Services (AWS)</h3>
              <p>
                We use AWS S3 to store and deliver images and assets. Your data may be processed on AWS servers located in the United States.
              </p>

              <h3>Render</h3>
              <p>
                Our website and client projects may be hosted on Render. Please review <a href="https://render.com/privacy" target="_blank" rel="noopener noreferrer">Render's Privacy Policy</a>.
              </p>
            </div>

            <div className="legal-section">
              <h2>5. Data Retention</h2>
              <p>We retain your personal information for as long as necessary to:</p>
              <ul>
                <li>Provide our services to you</li>
                <li>Comply with legal obligations</li>
                <li>Resolve disputes and enforce agreements</li>
              </ul>
              <p>
                Contact form submissions are retained in our database indefinitely for business records. You may request deletion of your data at any time (see Your Rights section).
              </p>
            </div>

            <div className="legal-section">
              <h2>6. Data Security</h2>
              <p>
                We implement appropriate technical and organizational measures to protect your personal information, including:
              </p>
              <ul>
                <li>SSL/TLS encryption for all data transmission</li>
                <li>Secure password practices and access controls</li>
                <li>Regular security updates and monitoring</li>
                <li>Limited access to personal data on a need-to-know basis</li>
              </ul>
              <p>
                However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </div>

            <div className="legal-section">
              <h2>7. Your Rights</h2>
              <p>Depending on your location, you may have the following rights regarding your personal information:</p>
              <ul>
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you.</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information.</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information.</li>
                <li><strong>Opt-out:</strong> Opt out of marketing communications at any time.</li>
                <li><strong>Data Portability:</strong> Request your data in a portable format.</li>
              </ul>
              <p>
                To exercise any of these rights, please contact us at james@silverfoxmedia.co. We will respond to your request within 30 days.
              </p>
            </div>

            <div className="legal-section">
              <h2>8. California Privacy Rights</h2>
              <p>
                If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):
              </p>
              <ul>
                <li>Right to know what personal information is collected, used, and shared</li>
                <li>Right to delete personal information</li>
                <li>Right to opt-out of the sale of personal information (we do not sell your data)</li>
                <li>Right to non-discrimination for exercising your privacy rights</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>9. Children's Privacy</h2>
              <p>
                Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a child, please contact us immediately so we can delete it.
              </p>
            </div>

            <div className="legal-section">
              <h2>10. International Data Transfers</h2>
              <p>
                Your information may be transferred to and processed in the United States, where our servers and third-party service providers are located. By using our services, you consent to this transfer.
              </p>
            </div>

            <div className="legal-section">
              <h2>11. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last updated" date. We encourage you to review this policy periodically.
              </p>
            </div>

            <div className="legal-section">
              <h2>12. Contact Us</h2>
              <p>
                If you have questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <ul className="legal-contact">
                <li><strong>Email:</strong> james@silverfoxmedia.co</li>
                <li><strong>Phone:</strong> (972) 800-8105</li>
                <li><strong>Website:</strong> silverfoxmedia.co</li>
              </ul>
            </div>

          </motion.div>
        </div>
      </section>
    </>
  );
};

export default PrivacyPolicy;
